import { makeEvent, SOURCE } from '../model';
import toEpochSeconds from '../timestamps';

// `payload` arrives as a JSON string; parse, then map each type to a row.

// UI telemetry and empty markers, excluded by default so diagnostic beats aren't
// buried. The page can opt them back in via a filter.
const DEFAULT_EXCLUDE = new Set([
  'SegmentEvent',
  'MixpanelEvent',
  'CheckpointEvent',
]);

// One payment can emit two SwipeEvents on the same userSessionId — a generic
// record and the Stripe Terminal one. Keep the Stripe one; the other's a dupe.
function chooseSwipes(records) {
  const keep = new Map(); // userSessionId -> { r, isStripe }
  for (const r of records) {
    if (r.eventType !== 'SwipeEvent') continue;
    const p = parsePayload(r.payload);
    const sid = p.userSessionId;
    if (!sid) continue;
    const isStripe = /stripe/i.test(p.customerName || '');
    const cur = keep.get(sid);
    if (!cur || (isStripe && !cur.isStripe)) keep.set(sid, { r, isStripe });
  }
  return keep;
}

export function fromFridgeEvents(
  records = [],
  { exclude = DEFAULT_EXCLUDE } = {},
) {
  const out = [];
  const swipeKeep = chooseSwipes(records);
  for (const r of records) {
    if (exclude.has(r.eventType)) continue;
    const ts = toEpochSeconds(r.timestamp);
    const p = parsePayload(r.payload);

    switch (r.eventType) {
      case 'PurchaseEvent': {
        const failed =
          Array.isArray(p.vendDetails) &&
          p.vendDetails.some((v) => (v.failed || []).length > 0);
        if (failed) {
          out.push(
            makeEvent({
              ts,
              source: SOURCE.EVENT,
              type: 'VEND_FAILED',
              title: 'Vend failed',
              summary: `Vend failure: ${describeFailed(p.vendDetails)}`,
              raw: p,
            }),
          );
          break;
        }
        // Kiosk vs mobile pickup. Key off the charge text, NOT lastFour — mobile
        // payments carry lastFour too.
        const isKiosk = /charged/i.test(p.summaryText || '');
        const amt =
          p.totalAmount != null && p.totalAmount !== ''
            ? ` — $${p.totalAmount}`
            : '';
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: isKiosk ? 'VEND_KIOSK' : 'VEND_MOBILE',
            title: `${isKiosk ? 'Kiosk purchase' : 'Mobile pickup'}${amt}`,
            summary: isKiosk
              ? p.summaryText || 'Paid in person at the fridge'
              : 'Reserve & pay (mobile) — picked up',
            raw: p,
          }),
        );
        break;
      }
      case 'SwipeEvent': {
        const sid = p.userSessionId;
        // Drop the duplicate swipe from the same payment session (kept the Stripe one).
        if (sid && swipeKeep.has(sid) && swipeKeep.get(sid).r !== r) break;
        const isStripe = /stripe/i.test(p.customerName || '');
        const last4 = p.lastFour || p.last4 || '----';
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'SWIPE',
            title: isStripe ? 'Card payment' : 'Card swipe',
            summary: `${p.cardType || 'Card'} ****${last4} — ${
              p.swipeStatus || 'swiped'
            }`,
            raw: p,
          }),
        );
        break;
      }
      case 'TempEvent': {
        const v = readTemp(p);
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'TEMP',
            title: v != null ? `Temp ${v}°` : 'Temp n/a',
            summary:
              v != null
                ? `Reported temperature ${v}°`
                : 'Reported temperature n/a',
            // Kept, not just rendered into the title: this is the number the chart
            // plots, and a null here is a reading that had none — which the chart
            // reports as a denominator rather than silently omitting.
            value: v,
            // normalize so downstream (UI + agent) always sees raw.temperature
            raw: p && typeof p === 'object' ? p : { temperature: v },
          }),
        );
        break;
      }
      case 'InventoryEvent': {
        const inv = summarizeInventory(p);
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'INVENTORY',
            title: 'Inventory snapshot',
            summary: inv.summary,
            raw: p,
          }),
        );
        break;
      }
      // Real Events-table shapes not yet in the live getFridgeEvents enum — ready
      // for the moment the backend exposes them.
      case 'VendUnitDoorEvent': {
        const open = /OPEN/i.test(p.state || '');
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: open ? 'DOOR_OPEN' : 'DOOR_CLOSE',
            summary: open ? 'Vend-unit door opened' : 'Vend-unit door closed',
            raw: p,
          }),
        );
        break;
      }
      case 'SafetyHoldEvent':
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'SAFETY_HOLD',
            severity: p.enabled ? undefined : 'info',
            title: p.enabled ? 'Safety hold tripped' : 'Safety hold released',
            summary: p.enabled
              ? `Engaged${
                  p.tempLimit != null ? ` (limit ${p.tempLimit}°)` : ''
                }${p.source ? ` · ${p.source}` : ''}`
              : 'Released',
            raw: p,
          }),
        );
        break;
      case 'OutOfOrderEvent':
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'OUT_OF_ORDER',
            severity: p.enabled ? undefined : 'info',
            title: p.enabled ? 'Marked out of order' : 'Back in service',
            summary: p.source ? `source: ${p.source}` : '',
            raw: p,
          }),
        );
        break;
      case 'VendFailedEvent':
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'VEND_FAILED',
            title: 'Vend failed',
            summary: `${
              (p.vendItem &&
                (p.vendItem.displayName || p.vendItem.vendItemName)) ||
              'item'
            } did not vend`,
            raw: p,
          }),
        );
        break;
      case 'VendItemEvent':
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'VEND_ITEM',
            summary: `Dispensed ${
              (p.vendItem &&
                (p.vendItem.displayName || p.vendItem.vendItemName)) ||
              'item'
            }`,
            raw: p,
          }),
        );
        break;
      case 'VendRetryEvent':
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'VEND_RETRY',
            summary: `${p.vendGatewayStatusMessage || 'retry'}${
              p.itemName ? ` · ${p.itemName}` : ''
            }`,
            raw: p,
          }),
        );
        break;
      case 'EmptyChannelReportEvent': {
        const slot = p.logicalSlot
          ? `(${p.logicalSlot.x},${p.logicalSlot.y})`
          : '';
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'EMPTY_CHANNEL',
            summary: `Empty-channel reset ${slot}`.trim(),
            raw: p,
          }),
        );
        break;
      }
      case 'AuthorizationEvent': {
        const failed = /fail/i.test(p.outcome || '');
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'AUTH',
            severity: failed ? 'warning' : 'info',
            title: `Card authorization — ${p.outcome || 'unknown'}`,
            summary: failed
              ? p.processorResponse || 'authorization failed'
              : '',
            raw: p,
          }),
        );
        break;
      }
      case 'CaptureEvent': {
        const failed = /fail/i.test(p.outcome || '');
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'CAPTURE',
            severity: failed ? 'warning' : 'info',
            title: `Payment capture — ${p.outcome || 'unknown'}`,
            summary: p.finalAmount != null ? `$${p.finalAmount}` : '',
            raw: p,
          }),
        );
        break;
      }
      case 'CouponAppliedEvent': {
        const c = p.coupon || {};
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'COUPON',
            summary: `Coupon ${c.couponCode || ''}${
              c.value ? ` (−${c.value})` : ''
            }`.trim(),
            raw: p,
          }),
        );
        break;
      }
      case 'ReservationEvent': {
        const r2 = p.reservationModel || p;
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'RESERVATION',
            summary: `Reservation ${r2.reservationCode || ''} created${
              Array.isArray(r2.vendItemNames)
                ? ` · ${r2.vendItemNames.length} item(s)`
                : ''
            }`.trim(),
            raw: p,
          }),
        );
        break;
      }
      case 'ReservationRedeemEvent':
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'RESERVATION_REDEEM',
            summary: `Reservation ${p.reservationCode || ''} redeemed`.trim(),
            raw: p,
          }),
        );
        break;
      case 'ReservationPickedUpEvent':
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'VEND_MOBILE',
            title: 'Mobile pickup',
            summary: `Reservation ${p.reservationCode || ''} picked up`.trim(),
            raw: p,
          }),
        );
        break;
      case 'ReservationNoShowEvent':
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'RESERVATION_NOSHOW',
            summary: `No-show · ${p.reservationCode || ''}`.trim(),
            raw: p,
          }),
        );
        break;
      case 'ReservationCanceledEvent':
        out.push(
          makeEvent({
            ts,
            source: SOURCE.EVENT,
            type: 'RESERVATION_CANCELED',
            summary: `Reservation ${p.reservationCode || ''} canceled`.trim(),
            raw: p,
          }),
        );
        break;
      default:
        // Not excluded, not specially handled — keep it as a generic row.
        out.push(
          makeEvent({ ts, source: SOURCE.EVENT, type: r.eventType, raw: p }),
        );
    }
  }
  return out;
}

function parsePayload(p) {
  if (p == null) return {};
  if (typeof p === 'object') return p;
  try {
    return JSON.parse(p);
  } catch {
    return {};
  }
}
// InventoryEvent carries a FULL fridge snapshot, not a delta — summarize to one
// line: { itemsBySlot: { "(row,col)": { itemsInSlot: [...] } } }
function summarizeInventory(p) {
  const slots = p && p.itemsBySlot ? Object.values(p.itemsBySlot) : [];
  let total = 0;
  let occupied = 0;
  let unavailable = 0;
  const products = new Set();
  for (const s of slots) {
    const items = (s && s.itemsInSlot) || [];
    if (items.length) occupied += 1;
    total += items.length;
    for (const it of items) {
      if (it.status && it.status !== 'AVAILABLE') unavailable += 1;
      if (it.vendItemName) products.add(it.vendItemName);
    }
  }
  let summary = `${total} items across ${occupied} slots · ${products.size} products`;
  if (unavailable) summary += ` · ${unavailable} unavailable`;
  return { summary, total, occupied, distinct: products.size, unavailable };
}

// TempEvent is a bare number (prod) or { temperature } / { value }. Normalize.
// Private: the parsed number is carried on the event as `value`, so the chart never
// needs to know how a fridge event stores a temperature.
function readTemp(p) {
  if (typeof p === 'number') return p;
  if (typeof p === 'string') {
    const n = Number(p);
    return Number.isFinite(n) ? n : null;
  }
  if (p && typeof p === 'object') {
    const v = p.temperature ?? p.value ?? p.temp;
    return v != null && v !== '' && Number.isFinite(Number(v))
      ? Number(v)
      : null;
  }
  return null;
}
function describeFailed(vd) {
  const names = vd
    .flatMap((v) => (v.failed || []).map((f) => f.vendItemName || f.itemName))
    .filter(Boolean);
  return names.join(', ') || 'item did not vend';
}
